/**
 * Click-to-seek and drag-to-seek scrub bar.
 *
 * Built on React Native's core `PanResponder` (no `react-native-gesture-handler`
 * dependency) — the responder system already works uniformly across native
 * touch and react-native-web's pointer emulation, and `a3s-ui` otherwise has
 * no gesture library dependency to match.
 */
import { memo, useCallback, useMemo, useState, type FC } from "react";
import { PanResponder, View, type GestureResponderEvent, type LayoutChangeEvent } from "react-native";
import "../../../theme";
import { StyleSheet } from "react-native-unistyles";
import { useLatestRef } from "../../../hooks/use-latest-ref";

export interface VideoPlayerScrubBarProps {
  readonly currentTime: number;
  readonly duration: number;
  readonly onSeek?: ((time: number) => void) | undefined;
  readonly onScrubStart?: (() => void) | undefined;
  readonly onScrubEnd?: (() => void) | undefined;
}

function clamp01(value: number): number {
  if (value < 0) return 0;
  if (value > 1) return 1;
  return value;
}

const VideoPlayerScrubBar: FC<VideoPlayerScrubBarProps> = memo(
  ({ currentTime, duration, onSeek, onScrubStart, onScrubEnd }) => {
    const [barWidth, setBarWidth] = useState(0);
    const [dragFraction, setDragFraction] = useState<number | null>(null);

    const durationRef = useLatestRef(duration);
    const barWidthRef = useLatestRef(barWidth);
    const onSeekRef = useLatestRef(onSeek);
    const onScrubStartRef = useLatestRef(onScrubStart);
    const onScrubEndRef = useLatestRef(onScrubEnd);
    const interactiveRef = useLatestRef(onSeek !== undefined && duration > 0);

    const seekToLocationX = useCallback(
      (locationX: number) => {
        const width = barWidthRef.current;
        if (width <= 0 || durationRef.current <= 0) return;
        const fraction = clamp01(locationX / width);
        setDragFraction(fraction);
        onSeekRef.current?.(fraction * durationRef.current);
      },
      [barWidthRef, durationRef, onSeekRef],
    );

    const panResponder = useMemo(
      () =>
        PanResponder.create({
          onStartShouldSetPanResponder: () => interactiveRef.current,
          onMoveShouldSetPanResponder: () => interactiveRef.current,
          onPanResponderGrant: (event: GestureResponderEvent) => {
            onScrubStartRef.current?.();
            seekToLocationX(event.nativeEvent.locationX);
          },
          onPanResponderMove: (event: GestureResponderEvent) => {
            seekToLocationX(event.nativeEvent.locationX);
          },
          onPanResponderRelease: () => {
            setDragFraction(null);
            onScrubEndRef.current?.();
          },
          onPanResponderTerminate: () => {
            setDragFraction(null);
            onScrubEndRef.current?.();
          },
        }),
      [interactiveRef, onScrubEndRef, onScrubStartRef, seekToLocationX],
    );

    const handleLayout = useCallback((event: LayoutChangeEvent) => {
      setBarWidth(event.nativeEvent.layout.width);
    }, []);

    if (duration <= 0) return null;

    const playedFraction = dragFraction ?? clamp01(currentTime / duration);
    // Pixel offsets from the measured bar width rather than a `${n}%` string —
    // sidesteps DimensionValue's narrow template-literal type without a cast.
    const filledWidth = playedFraction * barWidth;

    return (
      <View
        style={styles.hitArea}
        onLayout={handleLayout}
        accessibilityRole="adjustable"
        accessibilityLabel="Seek"
        accessibilityValue={{ min: 0, max: Math.round(duration), now: Math.round(currentTime) }}
        {...panResponder.panHandlers}
      >
        {/* Purely decorative — and deliberately non-interactive, so a touch can
            only ever land on the hit area itself.

            `seekToLocationX` divides `locationX` by the width `onLayout`
            measured on the hit area, so the two only agree while `locationX`
            is relative to that same element. On native it is relative to the
            *touched view*, so grabbing the 12px thumb (offset to
            `left: filledWidth`) would report 0–12px and slam playback back to
            the start. On web this happens to be safe already —
            react-native-web derives `locationX` from `currentTarget`, i.e. the
            responder, not the target — but relying on that would leave the
            native bug in place and make the maths silently platform-dependent. */}
        <View style={styles.track} pointerEvents="none">
          <View style={[styles.fill, { width: filledWidth }]} />
          <View style={[styles.thumb, { left: filledWidth }]} />
        </View>
      </View>
    );
  },
);

VideoPlayerScrubBar.displayName = "VideoPlayerScrubBar";

export { VideoPlayerScrubBar };

const THUMB_SIZE = 12;

const styles = StyleSheet.create((theme) => ({
  hitArea: {
    position: "absolute",
    bottom: 0,
    left: 0,
    right: 0,
    // Taller than the visible track so the touch target is comfortably larger
    // than the 4px bar it paints.
    height: theme.spacing.xl,
    justifyContent: "flex-end",
    zIndex: 10,
  },
  track: {
    height: 4,
    backgroundColor: theme.colors.mediaOverlay.scrubTrack,
  },
  fill: {
    position: "absolute",
    left: 0,
    top: 0,
    bottom: 0,
    backgroundColor: theme.colors.interactive.buttons.default_background,
  },
  thumb: {
    position: "absolute",
    top: -(THUMB_SIZE / 2 - 2),
    width: THUMB_SIZE,
    height: THUMB_SIZE,
    borderRadius: THUMB_SIZE / 2,
    marginLeft: -(THUMB_SIZE / 2),
    backgroundColor: theme.colors.interactive.buttons.default_background,
  },
}));
